import { Box, Button, Divider, GlobalStyles, Paper, Stack, Typography } from '@mui/material'
import { QRCodeSVG } from 'qrcode.react'
import type { ReactElement } from 'react'
import { useCallback } from 'react'
import { Helmet } from 'react-helmet'
import { IoCheckmarkCircle, IoNotificationsOutline, IoPhonePortraitOutline, IoPrintOutline } from 'react-icons/io5'
import { baseURL, deviceCampaignLabel, deviceCampaignPriceNumber, devicePriceNote, devicePriceNumber, serviceName, supportEmail } from 'src/config'

const TINT = '#FFF6E5'

export const Catalog = () => {
  const handlePrint = useCallback(() => {
    window.print()
  }, [])

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="robots" content="noindex, nofollow" />
        <title>{serviceName} | 製品カタログ</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      {/* 印刷 / PDF出力時のスタイル。ヘッダー・フッター・操作ボタンを隠しA4に最適化する */}
      <GlobalStyles
        styles={{
          '@media print': {
            // 背景色・背景画像・QRなどを印刷/PDFでもそのまま出力する
            '*': { WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' },
            'header, footer, .no-print': { display: 'none !important' },
            main: { marginTop: '0 !important' },
            'main > div': { marginTop: '0 !important' },
            body: { backgroundColor: '#fff' },
            img: { maxWidth: '100% !important' },
            '.catalog-root': { maxWidth: 'none !important' },
            '.catalog-page': { boxShadow: 'none !important', border: 'none !important', pageBreakInside: 'avoid' },
            '.catalog-avoid-break': { pageBreakInside: 'avoid' },
            // QRのSVGが印刷時に伸びて枠からはみ出るのを防ぐ
            '.catalog-qr': { padding: '20px !important' },
            '.catalog-qr svg': { width: '88px !important', height: '88px !important', display: 'block' },
          },
          '@page': { size: 'A4', margin: '14mm' },
        }}
      />

      <Box className="catalog-root">
        {/* 操作ボタン (印刷時は非表示) */}
        <Stack
          direction="row"
          className="no-print"
          sx={{
            justifyContent: 'flex-end',
            mb: 2,
          }}
        >
          <Button variant="contained" startIcon={<IoPrintOutline />} onClick={handlePrint} sx={{ borderRadius: 999, fontWeight: 700, boxShadow: 'none' }}>
            印刷 / PDF出力
          </Button>
        </Stack>

        <Stack spacing={4} className="catalog-page">
          <CatalogHeader />
          <HeroSection />
          <OverviewSection />
          <FeaturesSection />
          <SpecSection />
          <PriceSection />
          <NotificationSection />
          <ContactSection />
        </Stack>
      </Box>
    </>
  )
}

const CatalogHeader = () => {
  return (
    <Stack
      direction="row"
      className="catalog-avoid-break"
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: 'center',
        }}
      >
        <Box component="img" src="/logomark-color.svg" alt={serviceName} sx={{ height: 40 }} />
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 22, lineHeight: 1.1 }}>{serviceName}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            IoT罠とその管理サービス
          </Typography>
        </Box>
      </Stack>
      <Typography variant="overline" sx={{ color: 'primary.dark', fontWeight: 700 }}>
        製品カタログ
      </Typography>
    </Stack>
  )
}

const HeroSection = () => {
  return (
    <Paper
      elevation={0}
      className="catalog-avoid-break"
      sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider', background: `linear-gradient(135deg, ${TINT} 0%, #FFFFFF 60%)` }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{
          alignItems: 'stretch',
        }}
      >
        <Box sx={{ flex: 1, p: { xs: 3, sm: 4 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography variant="overline" sx={{ color: 'primary.dark' }}>
            LTEモデル
          </Typography>
          <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: 26, sm: 30 }, lineHeight: 1.3, mt: 0.5 }}>
            見回りの負担を軽減する
          </Typography>
          <Typography sx={{ mt: 2, fontSize: 14, color: 'text.secondary', lineHeight: 1.8 }}>
            本当に必要な機能だけを詰め込んだ、シンプルでお手頃なIoT罠デバイスとその管理サービス。親機・中継機は不要、単独で動作します。
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            width: '100%',
            minHeight: { xs: 200, sm: 240 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: (theme) => theme.palette.grey[300],
            p: 2,
          }}
        >
          <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box
              component="img"
              src="/wanapp_device_image.png"
              alt="LTEモデル"
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
            />
            <Typography variant="caption" sx={{ position: 'absolute', right: 8, bottom: 4, color: 'text.secondary' }}>
              ※画像はイメージです
            </Typography>
          </Box>
        </Box>
      </Stack>
    </Paper>
  )
}

const OverviewSection = () => {
  return (
    <Section eyebrow="ABOUT" title="製品概要">
      <Typography sx={{ fontSize: 14, lineHeight: 1.9, color: 'text.secondary' }}>
        {serviceName}
        は、くくり罠や箱罠に併設して使うIoT罠デバイスと、その管理Webアプリを提供するサービスです。LTE-M回線(スマートフォン等と同じ仕組み)でインターネットに接続し、罠の作動や不具合を検知して通知します。必要最低限の機能に絞ることで、使いやすさとお求めやすい価格を両立しました。
      </Typography>
    </Section>
  )
}

const FeaturesSection = () => {
  return (
    <Section eyebrow="FEATURE" title="特徴">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <FeatureCard
          icon={<IoPhonePortraitOutline />}
          title="Webアプリで一元管理"
          bullets={['Webアプリは無料で利用可能', 'デバイスの購入から管理までWebで完結', '個人から自治体まで、グループ共有にも対応']}
        />
        <FeatureCard
          icon={<IoNotificationsOutline />}
          title="必要な通知だけを確実に"
          bullets={['捕獲通知に加え故障・電池切れも検知', 'メール / 管理画面 / LINE に対応', '通信費は月額0円 (本体価格に含む)']}
        />
      </Stack>
    </Section>
  )
}

type FeatureCardProps = { icon: ReactElement; title: string; bullets: string[] }
const FeatureCard = ({ icon, title, bullets }: FeatureCardProps) => {
  return (
    <Paper elevation={0} sx={{ flex: 1, p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }} className="catalog-avoid-break">
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', fontSize: 24, color: 'primary.dark' }}>{icon}</Box>
        <Typography variant="h6" sx={{ fontSize: 17 }}>
          {title}
        </Typography>
      </Stack>
      <Stack
        spacing={1}
        sx={{
          mt: 2,
        }}
      >
        {bullets.map((b) => (
          <Stack
            key={b}
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'flex-start',
            }}
          >
            <Box sx={{ display: 'flex', color: 'primary.main', fontSize: 18, mt: '2px' }}>
              <IoCheckmarkCircle />
            </Box>
            <Typography sx={{ fontSize: 13.5, lineHeight: 1.6 }}>{b}</Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  )
}

const SpecSection = () => {
  const specs: { label: string; value: string }[] = [
    { label: 'モデル', value: 'LTEモデル' },
    { label: '寸法', value: '幅100 × 高さ150 × 奥行70 mm' },
    { label: '通信方法', value: 'LTE-M (スマートフォン等と同じ回線)' },
    { label: '通信費', value: '無料 (本体価格に含む / 月額0円)' },
    { label: '親機・中継機', value: '不要 (単独で動作)' },
    { label: '動力', value: '単3電池3本で約3〜4ヶ月稼働 (気候・電波状況で変動)' },
    { label: '通信目安', value: '概算10年間利用可能。上限到達時は有料(4,000円)で追加可能' },
    { label: '対応通知', value: '電源ON / 罠の作動 / 故障・電池切れの検知' },
    { label: '通知方法', value: 'メール / LINE / 管理画面' },
    { label: '利用可能エリア', value: 'LTE-M圏内 (圏外では利用不可)' },
  ]
  return (
    <Section eyebrow="SPEC" title="仕様">
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }} className="catalog-avoid-break">
        {specs.map((s, i) => (
          <Box key={s.label}>
            {i > 0 && <Divider />}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }} sx={{ px: 2.5, py: 1.5 }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', minWidth: { sm: 140 } }}>{s.label}</Typography>
              <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{s.value}</Typography>
            </Stack>
          </Box>
        ))}
      </Paper>
    </Section>
  )
}

const PriceSection = () => {
  return (
    <Section eyebrow="PRICE" title="料金">
      <Paper
        elevation={0}
        sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', backgroundColor: TINT, p: { xs: 3, sm: 4 } }}
        className="catalog-avoid-break"
      >
        <Box
          sx={{
            display: 'inline-block',
            px: 1.5,
            py: 0.5,
            mb: 1.5,
            borderRadius: 999,
            backgroundColor: 'primary.main',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {deviceCampaignLabel}
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2 }}
          sx={{
            alignItems: { sm: 'baseline' },
          }}
        >
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            デバイス本体価格
          </Typography>
          <Typography sx={{ color: 'text.secondary', textDecoration: 'line-through', fontSize: 18 }}>定価 {devicePriceNumber}円</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 40, color: 'primary.dark', lineHeight: 1 }}>{deviceCampaignPriceNumber}円</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            (税込)
          </Typography>
        </Stack>
        <Stack
          spacing={0.5}
          sx={{
            mt: 2.5,
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {devicePriceNote}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            ※毎月の通信費はかかりません。約10年分の通信費が本体価格に含まれています。
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            ※通信上限に達した場合、有料(4,000円)で追加通信が可能です (追加分も概算10年間利用可能)。
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            ※管理Webアプリは無料でご利用いただけます。
          </Typography>
        </Stack>
      </Paper>
    </Section>
  )
}

const NotificationSection = () => {
  return (
    <Section eyebrow="NOTIFICATION" title="通知の種類">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <NoticeCard title="電源ON" body="デバイスを起動したことを検知して通知します。" />
        <NoticeCard title="罠の作動 (捕獲)" body="罠が作動したことを検知し、いち早くお知らせします。" />
        <NoticeCard title="故障・電池切れ" body="不具合や電池切れの可能性を検知して通知します。" />
      </Stack>
    </Section>
  )
}

const NoticeCard = ({ title, body }: { title: string; body: string }) => {
  return (
    <Paper
      elevation={0}
      sx={{ flex: 1, p: 2.5, borderRadius: 3, border: '1px solid', borderLeftWidth: 4, borderLeftColor: 'primary.main', borderColor: 'divider' }}
      className="catalog-avoid-break"
    >
      <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 0.75 }}>{title}</Typography>
      <Typography sx={{ fontSize: 13, lineHeight: 1.7, color: 'text.secondary' }}>{body}</Typography>
    </Paper>
  )
}

const ContactSection = () => {
  const inquiryUrl = `${baseURL}/inquiry`
  return (
    <Section eyebrow="CONTACT" title="お問い合わせ">
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: { xs: 3, sm: 4 } }} className="catalog-avoid-break">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 3, sm: 5 }}
          sx={{
            alignItems: { sm: 'flex-start' },
            justifyContent: 'space-between',
          }}
        >
          <Stack spacing={2.5} sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 14, lineHeight: 1.9, color: 'text.secondary' }}>
              ご購入・ご相談はお気軽にお問い合わせください。
              <br />
              メール、または右のQRコードからお問い合わせページをご利用いただけます。
            </Typography>
            {supportEmail && <ContactRow label="メール" value={supportEmail} />}
          </Stack>
          <Stack
            spacing={1}
            sx={{
              alignItems: 'flex-start',
            }}
          >
            <Box className="catalog-qr" sx={{ p: 2.5, backgroundColor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider', lineHeight: 0 }}>
              <QRCodeSVG value={inquiryUrl} size={88} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                お問い合わせページ
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {inquiryUrl}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Paper>
    </Section>
  )
}

const ContactRow = ({ label, value }: { label: string; value: string }) => {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }}>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', minWidth: { sm: 100 }, pt: { sm: '2px' } }}>{label}</Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{value}</Typography>
    </Stack>
  )
}

type SectionProps = { eyebrow: string; title: string; children: React.ReactNode }
const Section = ({ eyebrow, title, children }: SectionProps) => {
  return (
    <Box className="catalog-avoid-break">
      <Typography variant="overline" sx={{ color: 'primary.dark', display: 'block' }}>
        {eyebrow}
      </Typography>
      <Typography variant="h6" sx={{ fontSize: 20, mb: 2 }}>
        {title}
      </Typography>
      {children}
    </Box>
  )
}
